import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const steps = [
  {
    id: 1,
    icon: 'ri-map-2-line',
    color: 'emerald',
    label: 'Modelo de Negócio',
    short: 'Entendimento Profundo',
    description:
      'Mergulhamos no seu negócio. Mapeamos processos, fluxos, gargalos e oportunidades antes de escrever uma linha de código ou propor qualquer solução.',
    detail: 'Entrevistas com stakeholders, análise de processos, mapeamento de fluxo de valor e diagnóstico completo do modelo operacional.',
    metric: 'Diagnóstico completo',
  },
  {
    id: 2,
    icon: 'ri-treasure-map-line',
    color: 'teal',
    label: 'Oportunidade Real',
    short: 'Onde está o ganho',
    description:
      'Identificamos onde a tecnologia gera retorno concreto. Priorizamos as oportunidades com maior impacto no resultado — eficiência, receita ou redução de custo.',
    detail: 'Análise de ROI potencial, priorização por impacto e esforço, definição de KPIs de sucesso e roadmap de implementação.',
    metric: 'ROI mapeado',
  },
  {
    id: 3,
    icon: 'ri-cpu-line',
    color: 'emerald',
    label: 'Aplicação de Tecnologia',
    short: 'Solução sob medida',
    description:
      'Aplicamos a tecnologia certa para cada desafio: IA, automação, software personalizado, BI ou integração de sistemas — sempre alinhado ao contexto do negócio.',
    detail: 'Desenvolvimento ágil, inteligência artificial aplicada, automação de processos, dashboards analíticos e integrações de sistemas.',
    metric: 'Solução entregue',
  },
  {
    id: 4,
    icon: 'ri-bar-chart-grouped-line',
    color: 'teal',
    label: 'Métrica de Melhoria',
    short: 'Resultado mensurável',
    description:
      'Medimos tudo. Cada solução entregue é acompanhada de indicadores claros que provam o impacto real no negócio — sem achismo, só dados.',
    detail: 'Monitoramento contínuo de KPIs, relatórios de performance, análise de desvios e ajustes baseados em evidências.',
    metric: 'Impacto comprovado',
  },
  {
    id: 5,
    icon: 'ri-refresh-line',
    color: 'emerald',
    label: 'Novo Ciclo',
    short: 'Evolução contínua',
    description:
      'Com os resultados em mãos, iniciamos um novo ciclo — mais informado, mais preciso. A empresa evolui continuamente, sempre um passo à frente.',
    detail: 'Revisão de roadmap, identificação de novas oportunidades, expansão das soluções e aprimoramento contínuo baseado nos dados coletados.',
    metric: 'Crescimento contínuo',
  },
];

const colorMap: Record<string, { bg: string; border: string; text: string; light: string; ring: string; dot: string }> = {
  emerald: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-500',
    text: 'text-emerald-600',
    light: 'bg-emerald-50',
    ring: 'ring-emerald-400',
    dot: 'bg-emerald-400',
  },
  teal: {
    bg: 'bg-teal-500',
    border: 'border-teal-500',
    text: 'text-teal-600',
    light: 'bg-teal-50',
    ring: 'ring-teal-400',
    dot: 'bg-teal-400',
  },
};

export default function CycleSection() {
  const [active, setActive] = useState(0);
  const { ref, visible } = useScrollAnimation();

  const current = steps[active];
  const c = colorMap[current.color];

  return (
    <section id="cycle" className="py-20 md:py-28 bg-zinc-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <span className="inline-block text-emerald-400 font-semibold text-sm font-inter tracking-widest uppercase mb-3">
            Nossa Metodologia
          </span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-white mb-4">
            O Ciclo de Valor <span className="text-emerald-400">Almadata</span>
          </h2>
          <p className="font-inter text-zinc-400 text-base max-w-2xl mx-auto">
            Um processo contínuo que transforma o entendimento do negócio em resultados mensuráveis — e cada resultado alimenta o próximo ciclo de evolução.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        {/* Cycle steps — horizontal nav */}
        <div
          className={`flex flex-wrap justify-center gap-3 mb-12 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {steps.map((step, i) => {
            const sc = colorMap[step.color];
            const isActive = active === i;
            return (
              <button
                key={step.id}
                onClick={() => setActive(i)}
                className={`group flex items-center gap-2 px-4 py-2.5 rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap font-inter text-sm font-medium ${
                  isActive
                    ? `${sc.bg} border-transparent text-white`
                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 bg-transparent'
                }`}
              >
                <span
                  className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-zinc-800 text-zinc-500 group-hover:bg-zinc-700'
                  }`}
                >
                  {step.id}
                </span>
                {step.label}
              </button>
            );
          })}
        </div>

        {/* Main content area */}
        <div
          className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
            {/* Step cards — left column */}
            <div className="lg:col-span-2 flex flex-col gap-3">
              {steps.map((step, i) => {
                const sc = colorMap[step.color];
                const isActive = active === i;
                return (
                  <button
                    key={step.id}
                    onClick={() => setActive(i)}
                    className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                      isActive
                        ? `border-${step.color}-500/50 bg-${step.color}-500/10`
                        : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
                    }`}
                    style={
                      isActive
                        ? {
                            borderColor: `rgba(${step.color === 'emerald' ? '16, 185, 129' : '20, 184, 166'}, 0.5)`,
                            backgroundColor: `rgba(${step.color === 'emerald' ? '16, 185, 129' : '20, 184, 166'}, 0.1)`,
                          }
                        : undefined
                    }
                  >
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300 ${
                        isActive ? sc.bg : 'bg-zinc-800'
                      }`}
                    >
                      <i
                        className={`${step.icon} text-lg transition-colors duration-300 ${
                          isActive ? 'text-white' : 'text-zinc-500'
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-sora font-semibold text-sm transition-colors duration-300 ${
                            isActive ? 'text-white' : 'text-zinc-400'
                          }`}
                        >
                          {step.label}
                        </span>
                        {isActive && (
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                        )}
                      </div>
                      <p className="font-inter text-xs text-zinc-500 mt-0.5 truncate">{step.short}</p>
                    </div>
                    <div
                      className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                        isActive ? `${sc.bg} text-white` : 'bg-zinc-800 text-zinc-600'
                      }`}
                    >
                      <i className="ri-arrow-right-s-line text-sm" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Detail panel — right column */}
            <div className="lg:col-span-3">
              <div
                key={active}
                className={`h-full rounded-2xl border transition-all duration-300 ${c.border}/30 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 md:p-10 flex flex-col justify-between animate-fade-in`}
              >
                {/* Top */}
                <div>
                  <div className="flex items-start gap-5 mb-6">
                    <div className={`w-16 h-16 flex items-center justify-center rounded-2xl ${c.bg} flex-shrink-0`}>
                      <i className={`${current.icon} text-white text-3xl`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-inter text-xs font-semibold tracking-widest uppercase ${c.text}`}>
                          Etapa {current.id} de {steps.length}
                        </span>
                      </div>
                      <h3 className="font-sora font-bold text-2xl md:text-3xl text-white leading-tight">
                        {current.label}
                      </h3>
                      <p className={`font-inter text-sm font-medium mt-1 ${c.text}`}>{current.short}</p>
                    </div>
                  </div>

                  <p className="font-inter text-zinc-300 text-base leading-relaxed mb-5">
                    {current.description}
                  </p>

                  <div
                    className={`rounded-xl border ${c.border}/20 p-4 transition-colors duration-300`}
                    style={{
                      backgroundColor: `rgba(${current.color === 'emerald' ? '16, 185, 129' : '20, 184, 166'}, 0.05)`,
                    }}
                  >
                    <p className="font-inter text-zinc-400 text-sm leading-relaxed">
                      <i className={`ri-information-line ${c.text} mr-2`} />
                      {current.detail}
                    </p>
                  </div>
                </div>

                {/* Bottom — progress + nav */}
                <div className="mt-8">
                  {/* Progress dots */}
                  <div className="flex items-center gap-2 mb-6">
                    {steps.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActive(i)}
                        className={`transition-all duration-300 rounded-full cursor-pointer ${
                          i === active
                            ? `h-2 w-8 ${c.bg}`
                            : 'h-2 w-2 bg-zinc-700 hover:bg-zinc-500'
                        }`}
                      />
                    ))}
                    <span className="ml-auto font-inter text-xs text-zinc-500">
                      {active + 1} / {steps.length}
                    </span>
                  </div>

                  {/* Nav buttons */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setActive((p) => Math.max(0, p - 1))}
                      disabled={active === 0}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-inter text-sm cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line" />
                      Anterior
                    </button>
                    <button
                      onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}
                      disabled={active === steps.length - 1}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-full ${c.bg} text-white hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-inter text-sm font-medium cursor-pointer whitespace-nowrap`}
                    >
                      Próximo
                      <i className="ri-arrow-right-line" />
                    </button>

                    {active === steps.length - 1 && (
                      <button
                        onClick={() => setActive(0)}
                        className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-all font-inter text-sm cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-refresh-line" />
                        Reiniciar ciclo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom connector — visual cycle indicator */}
        <div
          className={`mt-14 flex flex-wrap justify-center items-center gap-0 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {steps.map((step, i) => {
            const sc = colorMap[step.color];
            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => setActive(i)}
                  className={`flex flex-col items-center gap-1.5 px-3 py-2 rounded-xl transition-all duration-300 cursor-pointer group ${
                    active === i ? '' : 'opacity-50 hover:opacity-80'
                  }`}
                >
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${
                      active === i ? sc.bg : 'bg-zinc-800 group-hover:bg-zinc-700'
                    }`}
                  >
                    <i
                      className={`${step.icon} text-sm transition-colors duration-300 ${
                        active === i ? 'text-white' : 'text-zinc-500'
                      }`}
                    />
                  </div>
                  <span
                    className={`font-inter text-xs transition-colors duration-300 whitespace-nowrap ${
                      active === i ? 'text-white font-medium' : 'text-zinc-600'
                    }`}
                  >
                    {step.label}
                  </span>
                </button>
                {i < steps.length - 1 && (
                  <i className="ri-arrow-right-s-line text-zinc-700 text-lg mx-1" />
                )}
                {i === steps.length - 1 && (
                  <div className="flex items-center ml-1">
                    <i className="ri-arrow-right-s-line text-zinc-700 text-lg" />
                    <i className="ri-refresh-line text-emerald-700 text-sm ml-1" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
